import React from 'react';
import {inject, observer} from 'mobx-react';
import {observable} from 'mobx';
import clsx from 'clsx';
import config from './config';
import Login from './Login';
import {Session} from './app/Session';
import Draggable, {DraggableData, DraggableEvent} from 'react-draggable';
import {ConditionalWrapper} from './ConditionalWrapper';
import {secondsToTime} from './helpers';
import {Logger} from './app/Logger';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faMinus, faPhone, faSignOutAlt} from '@fortawesome/free-solid-svg-icons';
import Navigation from './components/Navigation';
import DetermineView from './components/views/DetermineView';
import View from './components/views/View';
import NavigationItem from './components/NavigationItem';
import HistoryView from './components/views/HistoryView';
import {callStore, CallStore} from './app/calls/CallStore';
import {faBackspace} from '@fortawesome/free-solid-svg-icons';
import {NotificationStore} from './app/notifications/NotificationStore';
import {NotificationObject} from './app/notifications/NotificationObject';
import Notification from './components/notifications/Notification';
import {Resource} from './app/services/json-api/Resource';
import {RegisteredCall} from './app/calls/RegisteredCall';
import {SipStore} from './app/services/sip/SipStore';
import {SoftphoneStore} from './app/softphone/SoftphoneStore';
import VoiceLogHandler from './components/calls/VoiceLogHandler';
import {VoiceLogStore} from './app/calls/voiceLogStore';
import {Variant} from "./components/notifications/Variant";
import {BrowserType, checkCurrentBrowser} from "./app/utils/checkCurrentBrowser";

export interface SoftphoneState {
    initialized: boolean;
    logoutDisabled: boolean,
    registration?: SIPml.Session.Registration;
    call?: SIPml.Session.Call;
    collapsed: boolean;
    incomingCall?: SIPml.Session.Call;
}

export interface SoftphoneProps extends React.HTMLAttributes<HTMLDivElement> {
    session?: Session;
    callStore?: CallStore;
    widget?: boolean;
    notifications?: NotificationStore;
    voiceLogStore?: VoiceLogStore;
    sipStore?: SipStore;
    softphoneStore?: SoftphoneStore;
}

export interface ParentCallDetail {
    number: string;
}

class SoftPhone extends React.Component<SoftphoneProps, SoftphoneState> {
    private remoteAudioRef: HTMLAudioElement | null = null;
    private incomingSound = new Audio(`${config.api.assets_url}/sounds/incoming.mp3`);
    private outgoingSound = new Audio(`${config.api.assets_url}/sounds/outgoing.mp3`);
    private callConfig: SIPml.Session.Configuration = {
        audio_remote: this.remoteAudioRef || undefined,
        events_listener: {
            events: '*', listener: (event: SIPml.Session.Event) => {
                this.onCallEvent(event)
            }
        }
    }
    public readonly state: SoftphoneState = {
        initialized: false,
        logoutDisabled: false,
        collapsed: false
    }
    private static CALL_INCORRECT_NUMBER = 'Address Incomplete';
    private static CALL_NUMBER_NOT_FOUND = 'Not Found';
    private static CALL_INCORRECT_NUMBER_MESSAGE = 'Nummer onjuist';
    private static REQUEST_CANCELLED = 'Request Cancelled';
    @observable private errors: { [key: string]: string[] } = {};
    @observable private timer = {
        seconds: 0
    }
    private timerInterval?: NodeJS.Timeout;
    private logger: Logger;
    @observable currentView: string = 'softphone';
    @observable private ready = true;
    @observable private number = '';

    private readonly currentBrowserType: BrowserType;
    public constructor(props: SoftphoneProps) {
        super(props);
        this.logger = new Logger();
        this.currentBrowserType = checkCurrentBrowser();
    }

    public async componentDidMount(): Promise<void> {
        const {
            session,
            widget,
            softphoneStore
        } = this.props;
        this.currentView = 'softphone';

        this.attachEvents(session!);

        session!.widget = !!widget;

        softphoneStore!.initialize((): void => {
            this.preInitialize();
        });
    }

    public componentDidUpdate(): void {
        const { session, sipStore } = this.props;

        if (session!.loggedIn && !sipStore!.sipStack) {
            this.ready = true;
        }
    }

    private attachEvents(session: Session): void {
        window.addEventListener("softphone:toggle-collapse", () => {
            this.toggleCollapse()
        });
        window.addEventListener("softphone:fetch-credentials", async () => {
            session.usesExpired = false;
            if (await session!.fetchCredentials()) {
                session!.loggedIn = true;
            }
        });
        window.addEventListener("softphone:kill", () => {
            this.props.session!.logout();
        })
        window.addEventListener("softphone:disable-logout", () => {
            this.setState({
                logoutDisabled: true
            })
        });

        window.addEventListener('beforeunload', (event: BeforeUnloadEvent) => this.handleBeforeUnload(event))
        window.addEventListener('unload', (event: Event) => this.handleUnload())
    }

    private setAudioRef(): void {
        if (!this.callConfig.audio_remote && this.remoteAudioRef) {
            this.callConfig.audio_remote = this.remoteAudioRef;
        }
    }

    private preInitialize(): void {
        this.setAudioRef();
        window.SIPml.init(() => this.startStack(), (e: Error) => this.onError(e));
        this.outgoingSound.loop = true;
        this.incomingSound.loop = true;
        this.setState(() => ({
            initialized: true
        }));
        this.ready = true;

        window.addEventListener('softphone:call-number', (event) => {
            const numberWithTel = (event as CustomEvent<ParentCallDetail>).detail.number;
            const number = numberWithTel.substr('tel:'.length, numberWithTel.length);

            if (this.state.collapsed) {
                this.toggleCollapse();
            }

            this.makeCallWithNumber(number);
        })
    }

    private logout(): void {
        const { sipStore } = this.props;

        this.clearErrors();
        if (sipStore!.sipStack) {
            sipStore!.sipStack.stop();
            sipStore!.sipStack = undefined;
        }

        this.props.session!.logout();
    }

    private makeCallWithNumber(number: string, callback?: () => void): void {
        this.clearErrors('number');
        this.number = number;
        this.makeCall();
    }

    private callNumberBack(previousCall: Resource<RegisteredCall>): void {
        if (previousCall.getAttribute('phone_number')) {
            this.number = previousCall.getAttribute('phone_number') as string
            this.makeCall(previousCall);
        }
    }

    private handleBeforeUnload = (event: BeforeUnloadEvent): void => {
        if(this.state.call) {
            event.returnValue = `Het gesprek wordt automatisch beindigd`;
        }
    }

    private handleUnload = (): void => {
        if(this.state.call) {
            this.hangUp();
        }
    }

    private onInitializeError(): void {
        this.ready = false;
        this.addError('stack', 'Geen verbinding met de server mogelijk', true);
    }

    private onStackStopped(): void {
        const { sipStore } = this.props;

        this.incomingSound.pause();
        this.outgoingSound.pause();
        sipStore!.sipStack = undefined;
        this.setState({
            call: undefined,
            incomingCall: undefined
        })
    }

    private onStackEvent(event: SIPml.Stack.Event): void {
        switch (event.type) {
            case 'started':
                this.login();
                break;
            case 'i_new_call':
                this.onIncomingCall(event);
                break;
            case 'failed_to_start':
                this.onInitializeError();
                break;
            case 'stopped':
                this.onStackStopped();
                break;
            case 'm_permission_refused':
                this.hangUp();
                this.props.notifications?.addNotification({
                    variant: Variant.DANGER,
                    message: 'Geen microfoon of luidspreker gevonden. Check of uw  browser de juiste rechten heeft',
                    autoClose: true,
                    type: 'm_permission_refused',
                    once: true
                });
                break
        }
    }

    private onIncomingCall(event: SIPml.Stack.Event): void {
        this.setAudioRef();

        if (this.state.call) {
            (event.newSession as SIPml.Session.Call).hangup();
            this.addMissingCall(event);
            return;
        }

        this.incomingSound.play();

        if (this.state.collapsed) {
            this.toggleCollapse();
        }

        this.currentView = 'softphone';

        this.setState({
            incomingCall: event.newSession as SIPml.Session.Call,
        }, () => {
            this.state.incomingCall!.setConfiguration(this.callConfig);
        })
    }

    private onCallEvent(event: SIPml.Session.Event): void {
        switch (event.type) {
            case 'connecting':
                window.dispatchEvent(new CustomEvent('softphone:call-started'));
                break;
            case 'connected':
                this.onCallAnswered();
                break;
            case 'i_ao_request':
                this.outgoingSound.pause();
                break;
            case 'terminated':
                this.onCallTerminated(event);
                break;
            case 'transport_error':
            case 'global_error':
            case 'message_error':
            case 'webrtc_error':
                this.logger.error(`Session ${event.type}: ${event.description}`);
        }
    }

    private onHangUpEvent(event: SIPml.Session.Event): void {
        //
    }

    private onRegistrationEvent(event: SIPml.Session.Event): void {
        switch (event.type) {
            case 'terminated':
                this.onInitializeError();
                break;
        }
    }

    private onCallTerminated(event: SIPml.Session.Event) {
        if (
            event.description === SoftPhone.CALL_INCORRECT_NUMBER
            || event.description === SoftPhone.CALL_NUMBER_NOT_FOUND
        ) {
            this.addError('number', SoftPhone.CALL_INCORRECT_NUMBER_MESSAGE);
        }

        this.hangUp();
    }

    private addMissingCall(event: SIPml.Stack.Event | SIPml.Session.Event): void {
        const { callStore } = this.props;

        if (Object.prototype.hasOwnProperty.call(event, 'newSession')) {
            callStore!.addMissedCall((event as SIPml.Stack.Event).newSession);
        } else {
            callStore!.addMissedCall((event as SIPml.Session.Event).session);
        }
    }

    private onCallAnswered(): void {
        if (!this.outgoingSound.paused) {
            this.outgoingSound.pause();
        }
    }

    private addError(name: string, message: string, clear?: boolean): void {
        if (clear || !Object.prototype.hasOwnProperty.call(this.errors, name)) {
            this.errors[name] = [];
        }

        this.errors[name].push(message);
    }

    private clearErrors(key?: string) {
        if (key && Object.prototype.hasOwnProperty.call(this.errors, key)) {
            delete this.errors[key];
        } else if (key === undefined) {
            this.errors = {};
        }
    }

    private startStack(): void {
        const { sipStore } = this.props;

        sipStore!.start(
            (e: SIPml.Stack.Event) => this.onStackEvent(e),
            (e: Error) => this.onError(e)
        );
    }

    private onError(e: Error): void {
        this.logger.error(e);
        this.ready = false;
        this.addError('stack', 'Verbinding met de de server is mislukt');
    }

    private login(): void {
        const { sipStore } = this.props;

        if (sipStore!.sipStack) {
            this.setState({
                registration: sipStore!.sipStack.newSession('register', {
                    events_listener: { events: '*', listener: (e: SIPml.Session.Event) => {
                        this.onRegistrationEvent(e)
                    } }
                })
            }, () => {
                this.state.registration!.register();
            })
        }
    }

    private fixNumber(): void {
        let newNumber = '';

        if (
            !this.number.startsWith('0')
            && !this.number.startsWith('+')
            && !this.number.startsWith('31')
        ) {
            newNumber = '0' + this.number;
        } else {
            newNumber = this.number;
        }

        newNumber = newNumber.replace(/-|\s|\+/g, '');

        this.number = newNumber;
    }

    private async makeCall(previousCall?: Resource<RegisteredCall>): Promise<void> {
        const { callStore, sipStore } = this.props;

        this.setAudioRef();
        this.fixNumber();
        if (sipStore!.sipStack && !this.state.call) {
            this.setState({
                call: sipStore!.sipStack.newSession('call-audio', this.callConfig) as SIPml.Session.Call
            }, () => {
                this.state.call!.call(this.number);
                this.startTimer();

                if (this.state.call) {
                    callStore!.startCall(this.state.call, previousCall);
                }
            })

            await this.outgoingSound.play();
        }
    }

    private hangUp(): void {
        const { callStore } = this.props;

        if (this.state.incomingCall) {
            callStore!.addMissedCall(this.state.incomingCall);
            this.state.incomingCall.hangup();
            this.setState({
                incomingCall: undefined
            });
        } else if (this.state.call) {
            this.state.call.hangup({ events_listener: { events: '*', listener: (e: SIPml.Session.Event) => {
                this.onHangUpEvent(e)
            } } });
            this.setState({
                call: undefined
            })
            callStore!.endCurrentCall();
            window.dispatchEvent(new CustomEvent('softphone:call-ended'));
        }

        this.stopTimer();

        if (!this.outgoingSound.paused) {
            this.outgoingSound.pause();
        }

        if (!this.incomingSound.paused) {
            this.incomingSound.pause();
        }
    }

    private addToNumber(c: string): void {
        this.number += c;
    }

    private removeNumber(): void {
        this.clearErrors('number');

        if (this.number.length && !this.state.call) {
           this.number = '';
        }
    }

    private answerIncomingCall(): void {
        const { callStore } = this.props;

        this.setAudioRef();
        this.incomingSound.pause();
        this.setState((prevState) => ({
            call: prevState.incomingCall,
            incomingCall: undefined
        }), () => {
            this.state.call!.accept();
            this.startTimer();
            callStore!.startCall(this.state.call!);
        })
    }

    private onDragStop(position: DraggableData): void {
        const { session } = this.props;
        session!.setWidgetSettings('position', { x: position.x, y: position.y });
    }

    private startTimer(): void {
        this.timerInterval = setInterval(() => {
            this.timer.seconds += 1;
        }, 1000);
    }

    private stopTimer(): void {
        this.timer.seconds = 0;

        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
    }

    private renderTimer(): string {
        const { seconds } = this.timer;
        const timeObj = secondsToTime(seconds);

        return `${timeObj.h}:${timeObj.m}:${timeObj.s}`;
    }

    private renderNotLoggedIn(): React.ReactNode {
        const { widget, session } = this.props;

        if (!widget) {
            if (!session!.voipCredentials) {
                return (
                    'De softphone kon niet geïnitialiseerd worden.'
                )
            }
        } else {
            return <Login
                onStackEvent={(event: SIPml.Stack.Event) => this.onStackEvent(event)}
                onError={(error: Error) => this.onError(error)}
            />
        }
    }

    private toggleCollapse(): void {
        this.setState((prevState) => ({collapsed: !prevState.collapsed}))
    }

    public render(): React.ReactNode {
        const {
            widget,
            session,
            notifications
        } = this.props;

        return (
            <>
                <ConditionalWrapper
                    condition={widget}
                    wrapper={(children) => (
                        <Draggable
                            bounds="html"
                            onStop={(_event: DraggableEvent, position: DraggableData) => this.onDragStop(position)}
                            defaultPosition={session!.getWidgetSettings('position') || undefined}
                            handle=".softphone-header"
                            defaultClassName={`react-draggable ${this.state.collapsed ? 'invisible' : ''}`}
                        >
                            {children}
                        </Draggable>
                    )}
                >
                    <div
                        className={clsx(
                            'softphone-wrapper',
                            widget && 'softphone-widget'
                        )}
                    >
                        <div className={`softphone-container ${this.props.session!.loggedIn ? '' : 'login'}`}>
                            <div className="softphone-toolbar softphone-header grid grid-cols-12">
                                {this.props.session!.loggedIn && this.props.widget && (
                                    <>
                                        <div className="col-span-2">
                                            {!this.state.call && (
                                                <button
                                                    disabled={!!this.state.call || this.state.logoutDisabled}
                                                    className={this.state.logoutDisabled ? 'invisible' : 'btn btn-darker-transparent ml-auto'}
                                                    onClick={() => {
                                                        this.logout();
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faSignOutAlt} />
                                                </button>
                                            )}
                                        </div>
                                        <div className="col-span-8">
                                    <span
                                        className="user"
                                    >
                                        Ingelogd als {this.props.session!.user?.name}
                                    </span>
                                        </div>
                                    </>
                                )}
                                <button
                                    onClick={() => this.toggleCollapse()}
                                    className={`collapse-softphone btn btn-darker-transparent ml-auto ${!this.props.session!.loggedIn && 'col-start-11'} col-span-2`}
                                >
                                    <FontAwesomeIcon icon={faMinus} />
                                </button>
                            </div>
                            {
                                ([BrowserType.CHROME, BrowserType.FIREFOX].indexOf(this.currentBrowserType) === -1) ? (
                                    <Notification
                                        variant={Variant.DANGER}
                                        message={`Uw huidige browser (${this.currentBrowserType}) wordt niet ondersteund. Gebruik indien mogelijk Google Chrome`}
                                        id={'Browser not supported'}
                                    />
                                ) : (
                                    this.props.session!.loggedIn ? (
                                        <>
                                            {notifications!.notifications.map((notification: NotificationObject) => (
                                                <Notification
                                                    variant={notification.variant}
                                                    message={notification.message}
                                                    id={notification.id}
                                                />
                                            ))}
                                            <Navigation>
                                                <NavigationItem
                                                    active={this.currentView === 'softphone'}
                                                    name="Softphone"
                                                    onClick={() => {
                                                        this.currentView = 'softphone';
                                                    }}
                                                />
                                            </Navigation>
                                            <DetermineView
                                                view={this.currentView}
                                            >
                                                <View name="softphone">
                                                    <div
                                                        className={clsx(
                                                            'softphone-number-container grid grid-cols-12',
                                                            this.errors.number && 'has-error'
                                                        )}
                                                    >
                                                        <div
                                                            className="col-span-7 col-start-3"
                                                        >
                                                            <input
                                                                name="softphone_number"
                                                                className="softphone-number-display"
                                                                placeholder="Start een nieuw gesprek"
                                                                value={this.number}
                                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                    this.number = event.target.value;
                                                                }}
                                                                type="tel"
                                                                disabled={!!this.state.call}
                                                            />
                                                        </div>
                                                        <div
                                                            className="col-span-1"
                                                        >
                                                            <button
                                                                className="btn btn-backspace"
                                                                onClick={() => {
                                                                    this.removeNumber();
                                                                }}
                                                            >
                                                                <FontAwesomeIcon
                                                                    className="icon-backspace"
                                                                    icon={faBackspace}
                                                                    style={{
                                                                        ['--fa-primary-color' as string]: '#fff',
                                                                        ['--fa-secondary-color' as string]: '#000',
                                                                        ['--fa-secondary-opacity' as string]: 1,
                                                                    }}
                                                                />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {this.errors.number && (
                                                        <div className="error-message">
                                                            {this.errors.number}
                                                        </div>
                                                    )}

                                                    <div
                                                        className="container-inner"
                                                    >
                                                        <div className="w-full block grid-cols-3 gap-4 my-4 call-actions">
                                                            <div>
                                                                {!!this.state.call && (<VoiceLogHandler />)}
                                                            </div>
                                                            <div className="flex items-center">{this.renderTimer()}</div>
                                                        </div>

                                                        <div className="numbers-grid grid grid-cols-3 grid-rows-3 gap-4 grid-flow-row">
                                                            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((c: string) => (
                                                                <button
                                                                    key={c}
                                                                    type="button"
                                                                    className="numbers-grid-item"
                                                                    onClick={() => this.addToNumber(c)}
                                                                >
                                                                    {c}
                                                                </button>
                                                            ))}
                                                        </div>
                                                        {this.ready ? (
                                                            <div className="softphone-actions grid grid-cols-6 grid-rows-1 gap-4 grid-flow-row">
                                                                {
                                                                    this.state.incomingCall ? (
                                                                        <>
                                                                            <button
                                                                                className="btn btn-end-call col-span-3"
                                                                                disabled={!this.state.incomingCall && !this.state.call}
                                                                                onClick={() => {
                                                                                    this.hangUp();
                                                                                }}
                                                                            >
                                                                                <FontAwesomeIcon className='icon-end-call' icon={faPhone} />
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-accept-call col-span-3"
                                                                                onClick={() => {
                                                                                    this.answerIncomingCall();
                                                                                }}
                                                                            >
                                                                                <FontAwesomeIcon className='icon-accept-call'
                                                                                                 icon={faPhone}/>
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        this.state.call ? (
                                                                            <button
                                                                                className="btn btn-end-call col-span-3"
                                                                                disabled={!this.state.incomingCall && !this.state.call}
                                                                                onClick={() => {
                                                                                    this.hangUp();
                                                                                }}
                                                                            >
                                                                                <FontAwesomeIcon className='icon-end-call' icon={faPhone} />
                                                                            </button>
                                                                        ) : (
                                                                            <button
                                                                                className="btn btn-accept-call col-start-4 col-span-3"
                                                                                disabled={!!this.state.call || !this.number}
                                                                                onClick={() => {
                                                                                    this.makeCall();
                                                                                }}
                                                                            >
                                                                                <FontAwesomeIcon className='icon-accept-call'
                                                                                                 icon={faPhone}/>
                                                                            </button>
                                                                        )
                                                                    )
                                                                }
                                                            </div>
                                                        ) : (
                                                            <div className="error-message mt-4">
                                                                {this.errors.stack}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <audio id="audio-remote" ref={(ref) => {
                                                        this.remoteAudioRef = ref;
                                                    }} autoPlay={true} />
                                                </View>
                                                <View
                                                    name="history"
                                                >
                                                    <HistoryView
                                                        disableCallBack={!!this.state.call}
                                                        onCallBack={(previousCall: Resource<RegisteredCall>) => {
                                                            if (previousCall && !this.state.call) {
                                                                this.currentView = 'softphone';
                                                                this.callNumberBack(previousCall);
                                                            }
                                                        }}
                                                    />
                                                </View>
                                            </DetermineView>
                                        </>
                                    ) : (
                                        this.renderNotLoggedIn()
                                    )
                                )
                            }
                        </div>
                    </div>
                </ConditionalWrapper>
                <button
                    onClick={() => this.toggleCollapse()}
                    className={`softphone-collapsed-widget ${this.state.collapsed ? '' : 'invisible'}`}
                >
                    <div className="qble-logo" />
                </button>
            </>
        )
    }
}

export default inject(
    'session',
    'callStore',
    'notifications',
    'sipStore',
    'softphoneStore',
    'voiceLogStore'
)(observer(SoftPhone));
